import { useState } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  savePublishedProtocol,
  type PublishedProtocolFood,
  type PublishedProtocolRule,
} from '../../utils/publishedProtocolsStore';
import {
  getProtocolBuilderStepContent,
  type FoodClassification,
  type ProtocolRule,
  type AssignedPatient,
} from './protocolBuilder/stepContent';
import { commonFoods, isProtocolBuilderStepValid } from './protocolBuilder/constants';

const steps = ['Select Patient', 'Protocol Basics', 'Stoplight Classifications', 'Protocol Rules', 'Review & Publish'];

const getTodayIsoDate = () => new Date().toISOString().split('T')[0];

export const ProtocolBuilderPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState<AssignedPatient | null>(null);
  const [protocolTitle, setProtocolTitle] = useState('');
  const [protocolDescription, setProtocolDescription] = useState('');
  const [baseTemplate, setBaseTemplate] = useState('template-standard-t2d');
  const [protocolStatus, setProtocolStatus] = useState<'ACTIVE' | 'ARCHIVED'>('ACTIVE');
  const [startDate, setStartDate] = useState(getTodayIsoDate());
  const [endDate, setEndDate] = useState('');
  const [classifications, setClassifications] = useState<FoodClassification[]>([]);
  const [rules, setRules] = useState<ProtocolRule[]>([]);
  const [openFoodDialog, setOpenFoodDialog] = useState(false);
  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [editingFood, setEditingFood] = useState<FoodClassification | null>(null);
  const [editingRule, setEditingRule] = useState<ProtocolRule | null>(null);

  const [foodName, setFoodName] = useState('');
  const [foodClassification, setFoodClassification] = useState<'GREEN' | 'YELLOW' | 'RED'>('GREEN');
  const [foodPortion, setFoodPortion] = useState('');
  const [foodNotes, setFoodNotes] = useState('');

  const [ruleType, setRuleType] = useState<'MEAL_TIMING' | 'PORTION_LIMIT' | 'PAIRING' | 'AVOIDANCE' | 'CUSTOM'>('PORTION_LIMIT');
  const [ruleDescription, setRuleDescription] = useState('');
  const [ruleParameters, setRuleParameters] = useState<Record<string, string | number | boolean>>({});

  const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);
  const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

  const handleReset = () => {
    setActiveStep(0);
    setSelectedPatient(null);
    setProtocolTitle('');
    setProtocolDescription('');
    setBaseTemplate('template-standard-t2d');
    setProtocolStatus('ACTIVE');
    setStartDate(getTodayIsoDate());
    setEndDate('');
    setClassifications([]);
    setRules([]);
  };

  const handleOpenFoodDialog = (food?: FoodClassification) => {
    if (food) {
      setEditingFood(food);
      setFoodName(food.customFoodName);
      setFoodClassification(food.classification);
      setFoodPortion(food.portion);
      setFoodNotes(food.notes);
    } else {
      setEditingFood(null);
      setFoodName('');
      setFoodClassification('GREEN');
      setFoodPortion('');
      setFoodNotes('');
    }
    setOpenFoodDialog(true);
  };

  const handleCloseFoodDialog = () => {
    setOpenFoodDialog(false);
    setEditingFood(null);
  };

  const handleSaveFood = () => {
    const newFood: FoodClassification = {
      id: editingFood ? editingFood.id : `class-${Date.now()}`,
      customFoodName: foodName,
      classification: foodClassification,
      portion: foodPortion,
      notes: foodNotes,
    };

    if (editingFood) {
      setClassifications(classifications.map((f) => (f.id === editingFood.id ? newFood : f)));
    } else {
      setClassifications([...classifications, newFood]);
    }

    handleCloseFoodDialog();
  };

  const handleDeleteFood = (id: string) => setClassifications(classifications.filter((f) => f.id !== id));

  const handleOpenRuleDialog = (rule?: ProtocolRule) => {
    if (rule) {
      setEditingRule(rule);
      setRuleType(rule.ruleType);
      setRuleDescription(rule.description);
      setRuleParameters(rule.parameters);
    } else {
      setEditingRule(null);
      setRuleType('PORTION_LIMIT');
      setRuleDescription('');
      setRuleParameters({});
    }
    setOpenRuleDialog(true);
  };

  const handleCloseRuleDialog = () => {
    setOpenRuleDialog(false);
    setEditingRule(null);
  };

  const handleSaveRule = () => {
    const newRule: ProtocolRule = {
      id: editingRule ? editingRule.id : `rule-${Date.now()}`,
      ruleType,
      description: ruleDescription,
      parameters: ruleParameters,
      isActive: true,
    };

    if (editingRule) {
      setRules(rules.map((r) => (r.id === editingRule.id ? newRule : r)));
    } else {
      setRules([...rules, newRule]);
    }

    handleCloseRuleDialog();
  };

  const handleDeleteRule = (id: string) => setRules(rules.filter((r) => r.id !== id));

  const handlePublish = () => {
    if (!selectedPatient) return;

    const createdDate = new Date().toISOString();
    savePublishedProtocol({
      id: `protocol-${Date.now()}`,
      patientId: selectedPatient.id,
      patientName: `${selectedPatient.firstName} ${selectedPatient.lastName}`,
      title: protocolTitle.trim(),
      description: protocolDescription.trim(),
      templateId: baseTemplate,
      status: protocolStatus,
      sharedWithPatient: false,
      createdDate,
      startDate,
      endDate: endDate || undefined,
      classifications: classifications as PublishedProtocolFood[],
      rules: rules as PublishedProtocolRule[],
    });

    handleNext();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Protocol Builder
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Create personalized stoplight food protocols based on CGM data and patient response patterns
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getProtocolBuilderStepContent({
          step: activeStep,
          selectedPatient,
          setSelectedPatient,
          protocolTitle,
          setProtocolTitle,
          protocolDescription,
          setProtocolDescription,
          baseTemplate,
          setBaseTemplate,
          protocolStatus,
          setProtocolStatus,
          startDate,
          setStartDate,
          endDate,
          setEndDate,
          classifications,
          rules,
          handleOpenFoodDialog,
          handleDeleteFood,
          handleOpenRuleDialog,
          handleDeleteRule,
          handleReset,
        })}

        {activeStep !== 5 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={activeStep === 4 ? handlePublish : handleNext}
              disabled={!isProtocolBuilderStepValid(
                activeStep,
                selectedPatient !== null,
                protocolTitle,
                startDate,
                classifications.length
              )}
            >
              {activeStep === 4 ? 'Publish Protocol' : 'Next'}
            </Button>
          </Box>
        )}
      </Paper>

      <Dialog open={openFoodDialog} onClose={handleCloseFoodDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingFood ? 'Edit Food Classification' : 'Add Food Classification'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              freeSolo
              options={commonFoods}
              value={foodName}
              onChange={(_, newValue) => setFoodName(newValue || '')}
              onInputChange={(_, newValue) => setFoodName(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Food Item" placeholder="Start typing or select from common foods" />
              )}
            />
            <FormControl fullWidth>
              <InputLabel>Classification</InputLabel>
              <Select
                value={foodClassification}
                label="Classification"
                onChange={(e) => setFoodClassification(e.target.value as 'GREEN' | 'YELLOW' | 'RED')}
              >
                <MenuItem value="GREEN">GREEN - Eat Freely</MenuItem>
                <MenuItem value="YELLOW">YELLOW - Moderate</MenuItem>
                <MenuItem value="RED">RED - Avoid/Limit</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Recommended Portion"
              value={foodPortion}
              onChange={(e) => setFoodPortion(e.target.value)}
              placeholder="e.g., 1/2 cup, 1 medium, etc."
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (optional)"
              value={foodNotes}
              onChange={(e) => setFoodNotes(e.target.value)}
              placeholder="Add personalized notes about this food..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFoodDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFood} disabled={!foodName || !foodPortion}>
            {editingFood ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRuleDialog} onClose={handleCloseRuleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRule ? 'Edit Protocol Rule' : 'Add Protocol Rule'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Rule Type</InputLabel>
              <Select
                value={ruleType}
                label="Rule Type"
                onChange={(e) => setRuleType(e.target.value as 'MEAL_TIMING' | 'PORTION_LIMIT' | 'PAIRING' | 'AVOIDANCE' | 'CUSTOM')}
              >
                <MenuItem value="MEAL_TIMING">Meal Timing</MenuItem>
                <MenuItem value="PORTION_LIMIT">Portion Limit</MenuItem>
                <MenuItem value="PAIRING">Food Pairing</MenuItem>
                <MenuItem value="AVOIDANCE">Avoidance</MenuItem>
                <MenuItem value="CUSTOM">Custom Rule</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Rule Description"
              value={ruleDescription}
              onChange={(e) => setRuleDescription(e.target.value)}
              placeholder="Describe the rule in plain language..."
            />
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              <strong>Examples:</strong>
              <br />• "Limit YELLOW foods to max 1 per meal"
              <br />• "Avoid carb-heavy snacks after 8pm"
              <br />• "Always pair pasta with protein and vegetables"
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRuleDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRule} disabled={!ruleDescription}>
            {editingRule ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
